import { Phone, Mail, MapPin, Clock } from "lucide-react";
import PageHeader from "../components/PageHeader";
import { SITE } from "../lib/site";
import { Seo, localBusinessSchema } from "../components/Seo";

export default function Contact() {
  return (
    <div data-testid="page-contact">
      <Seo
        path="/contact"
        title={`Contact — Call ${SITE.contact.phone} · Hermiston, OR`}
        description={`Reach Dirty Wrenches Automotive at ${SITE.contact.phone} or ${SITE.contact.email}. 1055 N 1st Pl, Suite 3A, Hermiston, OR 97838. Mon–Fri 9–5. BMW, Audi, VW, Mercedes, Subaru, Porsche specialists.`}
        schema={localBusinessSchema}
      />
      <PageHeader
        code="// 05"
        kicker="Contact"
        title={<>Stop in.<br />Or just call.</>}
        subtitle="The fastest way is the phone. The bays are loud — we'll call you back inside the hour if we miss it."
        testId="contact-header"
      />

      <section className="px-6 md:px-12 lg:px-24 py-20 md:py-28">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-px bg-white/10 border border-white/10">
          <a
            href={`tel:${SITE.contact.phone}`}
            data-testid="contact-phone"
            className="lg:col-span-4 group bg-[#0A0A0A] hover:bg-[#1A1F24] transition-colors p-8 md:p-10 flex flex-col"
          >
            <Phone className="w-7 h-7 text-[#FF3B30]" />
            <div className="mt-6 font-mono-tech text-[10px] text-white/40 tracking-[0.3em] uppercase">Call the shop</div>
            <div className="mt-2 font-display font-black uppercase tracking-tight text-2xl md:text-3xl">{SITE.contact.phone}</div>
            <div className="mt-4 text-white/55 text-sm">Best for: scheduling, urgent diagnostics, picking up.</div>
          </a>
          <a
            href={`mailto:${SITE.contact.email}`}
            data-testid="contact-email"
            className="lg:col-span-4 group bg-[#0A0A0A] hover:bg-[#1A1F24] transition-colors p-8 md:p-10 flex flex-col"
          >
            <Mail className="w-7 h-7 text-[#FF3B30]" />
            <div className="mt-6 font-mono-tech text-[10px] text-white/40 tracking-[0.3em] uppercase">Email the shop</div>
            <div className="mt-2 font-display font-black uppercase tracking-tight text-xl md:text-2xl break-all">{SITE.contact.email}</div>
            <div className="mt-4 text-white/55 text-sm">Best for: VIN/quote requests, attaching scan reports.</div>
          </a>
          <div
            data-testid="contact-address"
            className="lg:col-span-4 bg-[#0A0A0A] p-8 md:p-10 flex flex-col"
          >
            <MapPin className="w-7 h-7 text-[#FF3B30]" />
            <div className="mt-6 font-mono-tech text-[10px] text-white/40 tracking-[0.3em] uppercase">Drop by the bay</div>
            <div className="mt-2 font-display font-black uppercase tracking-tight text-xl md:text-2xl leading-tight">{SITE.contact.address}</div>
            <div className="mt-4 text-white/55 text-sm">Roll-up door 3. Park anywhere in the lot — we're the loud one.</div>
          </div>
        </div>

        <div className="mt-12 grid grid-cols-1 lg:grid-cols-12 gap-8">
          <div className="lg:col-span-5 bg-[#1A1F24] border border-white/10 p-8 md:p-10">
            <div className="flex items-center gap-3">
              <Clock className="w-6 h-6 text-[#FF3B30]" />
              <div className="font-mono-tech text-[11px] text-[#FF3B30] tracking-[0.3em] uppercase">// Hours</div>
            </div>
            <h3 className="mt-4 font-display font-black uppercase tracking-tighter text-3xl md:text-4xl">When we're open.</h3>
            <ul className="mt-6 divide-y divide-white/10">
              {SITE.contact.hours.map((h) => (
                <li key={h.d} data-testid={`hours-${h.d.toLowerCase().replace(/\s|—/g, "-")}`} className="flex items-center justify-between py-4">
                  <span className="font-display font-bold uppercase tracking-tight text-base md:text-lg">{h.d}</span>
                  <span className="font-mono-tech text-sm text-white/70">{h.h}</span>
                </li>
              ))}
            </ul>
            <p className="mt-6 text-white/55 text-sm leading-relaxed">
              We typically book two to three weeks out. Tell us what's wrong with the car when you call — we may be able to slot in quick diagnostics sooner.
            </p>
          </div>

          <div className="lg:col-span-7 bg-[#1A1F24] border border-white/10 overflow-hidden">
            <div className="p-6 md:p-8 border-b border-white/10">
              <div className="font-mono-tech text-[11px] text-[#FF3B30] tracking-[0.3em] uppercase">// Where we are</div>
              <h3 className="mt-3 font-display font-black uppercase tracking-tighter text-3xl md:text-4xl">Find the shop.</h3>
            </div>
            <div className="relative aspect-[16/10] bg-black">
              <iframe
                data-testid="contact-map"
                title="Dirty Wrenches location"
                src="https://www.openstreetmap.org/export/embed.html?bbox=-119.2950%2C45.8330%2C-119.2700%2C45.8470&layer=mapnik&marker=45.8400%2C-119.2825"
                className="absolute inset-0 w-full h-full grayscale-[40%] contrast-110"
                loading="lazy"
              />
              <div className="absolute inset-0 ring-1 ring-inset ring-white/10 pointer-events-none" />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
